import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import ConfirmModal from '@/components/ConfirmModal';
import Toast from '@/components/Toast';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { articleAdminApi } from '@/features/admin/articleAdminApi';
import { useAuth } from '@/contexts/AuthContext';
import { Article } from '@/types/article';

type TtsLevel = 'A' | 'B' | 'C';
type VideoAspect = '9x16' | '16x9';
type VoiceGender = 'male' | 'female';
type TtsConfirmState = { level: TtsLevel; mode: 'full' | 'articleOnly' } | null;
type IpaConfirmState = { level: TtsLevel } | null;
type ImageConfirmState = { regenerate: true } | null;
const SUBTITLE_LEAD_STORAGE_KEY = 'ttsReview.subtitleLeadSeconds';

export const TtsReview: React.FC = () => {
  const { country, id: articleId } = useParams<{ country: string; id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [generatingVideoLevel, setGeneratingVideoLevel] = useState<TtsLevel | null>(null);
  const [generatingVideoAspect, setGeneratingVideoAspect] = useState<VideoAspect | null>(null);
  const [videoTitleInputs, setVideoTitleInputs] = useState<Record<TtsLevel, string>>({
    A: '',
    B: '',
    C: '',
  });
  const [videoProgressText, setVideoProgressText] = useState('');
  const [monologueVoiceGenders, setMonologueVoiceGenders] = useState<Record<TtsLevel, VoiceGender>>(
    {
      A: 'female',
      B: 'female',
      C: 'female',
    }
  );
  const [dialogueVoiceGenders, setDialogueVoiceGenders] = useState<
    Record<TtsLevel, { A: VoiceGender; B: VoiceGender }>
  >({
    A: { A: 'male', B: 'female' },
    B: { A: 'male', B: 'female' },
    C: { A: 'male', B: 'female' },
  });
  const [article, setArticle] = useState<Article | null>(null);
  const [notification, setNotification] = useState<{
    message: string;
    type?: 'success' | 'error' | 'info';
  } | null>(null);
  const [showPublishConfirm, setShowApproveConfirm] = useState(false);
  const [ttsConfirm, setTtsConfirm] = useState<TtsConfirmState>(null);
  const [ipaConfirm, setIpaConfirm] = useState<IpaConfirmState>(null);
  const [imageConfirm, setImageConfirm] = useState<ImageConfirmState>(null);
  const [regeneratingLevels, setRegeneratingLevels] = useState<Set<TtsLevel>>(new Set());
  const [regeneratingArticleOnlyLevels, setRegeneratingArticleOnlyLevels] = useState<Set<TtsLevel>>(
    new Set()
  );
  const [regeneratingSentenceLevels, setRegeneratingSentenceLevels] = useState<Set<TtsLevel>>(
    new Set()
  );
  const [regeneratingIpaLevels, setRegeneratingIpaLevels] = useState<Set<TtsLevel>>(new Set());
  const [isRegeneratingImage, setIsRegeneratingImage] = useState(false);
  const [isMarkingAsPosted, setIsMarkingAsPosted] = useState(false);
  const [playingSentenceKey, setPlayingSentenceKey] = useState<string | null>(null);
  const [subtitleLeadSeconds, setSubtitleLeadSeconds] = useState<number | null>(() => {
    const raw = localStorage.getItem(SUBTITLE_LEAD_STORAGE_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return null;
    return Math.max(0, Math.min(2, parsed));
  });
  const notifTimeout = useRef<number | null>(null);
  const sentenceAudioRef = useRef<HTMLAudioElement | null>(null);

  const getLevelLabel = (level: 'A' | 'B' | 'C'): string => {
    if (level === 'A') return 'Easy';
    if (level === 'B') return 'Medium';
    return 'Hard';
  };

  const showNotification = (
    message: string,
    type: 'success' | 'error' | 'info' = 'info',
    duration = 3000
  ) => {
    setNotification({ message, type });
    if (notifTimeout.current) window.clearTimeout(notifTimeout.current);
    notifTimeout.current = window.setTimeout(() => setNotification(null), duration);
  };

  useEffect(() => {
    return () => {
      if (notifTimeout.current) window.clearTimeout(notifTimeout.current);

      if (sentenceAudioRef.current) {
        sentenceAudioRef.current.pause();
        sentenceAudioRef.current.src = '';
      }

      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const fetchArticleDetail = async (country: string, articleId: string) => {
    setIsLoading(true);
    try {
      const data = await articleAdminApi.getById(country, articleId);
      setArticle(data);
    } catch (error) {
      showNotification('Failed to fetch article details', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (articleId) {
      fetchArticleDetail(country || '', articleId);
    }
  }, [country, articleId]);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      if (!article) throw new Error('No article');
      const payload = { ...article } as Article;
      if (!isVi) {
        payload.ipaALevelEN = ipaAState.en;
        payload.ipaBLevelEN = ipaBState.en;
        payload.ipaCLevelEN = ipaCState.en;
      } else {
        payload.ipaALevelUS = ipaAState.us;
        payload.ipaBLevelUS = ipaBState.us;
        payload.ipaCLevelUS = ipaCState.us;
      }
      setArticle(payload);
      await articleAdminApi.update(country!, articleId!, payload);
      navigate('/editor/articles', {
        state: {
          notification: { message: 'Article saved successfully!', type: 'success' },
          activeTab: 'tts',
        },
      });
    } catch (error) {
      showNotification('Failed to save article', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = () => {
    setShowApproveConfirm(true);
  };

  const performApprove = async () => {
    if (!article) return;
    article.isTtsReviewed = true;
    article.ttsReviewedBy = user?.name || 'Unknown';
    handleSave();
  };

  const isRegeneratingLevel = (level: TtsLevel): boolean => regeneratingLevels.has(level);
  const isRegeneratingArticleOnlyLevel = (level: TtsLevel): boolean =>
    regeneratingArticleOnlyLevels.has(level);
  const isRegeneratingSentenceLevel = (level: TtsLevel): boolean =>
    regeneratingSentenceLevels.has(level);

  const handleRegenerateTts = async (level: TtsLevel) => {
    if (!articleId || isRegeneratingLevel(level) || !article) return;

    setRegeneratingLevels(prev => new Set(prev).add(level));

    try {
      const ttsRegenerateOptions =
        article.articleType === 'dialogue'
          ? {
              dialogueVoiceGenders: {
                A: dialogueVoiceGenders[level].A,
                B: dialogueVoiceGenders[level].B,
              },
            }
          : {
              voiceGender: monologueVoiceGenders[level],
            };

      const updated = await articleAdminApi.regenerateTts(
        country || '',
        articleId,
        level,
        ttsRegenerateOptions
      );
      setArticle(updated);
      showNotification(`TTS level ${level} regenerated successfully`, 'success');
    } catch (error) {
      showNotification(
        error instanceof Error ? error.message : `Failed to regenerate TTS level ${level}`,
        'error'
      );
    } finally {
      setRegeneratingLevels(prev => {
        const next = new Set(prev);
        next.delete(level);
        return next;
      });
    }
  };

  const handleRegenerateArticleOnlyTts = async (level: TtsLevel) => {
    if (
      !articleId ||
      isRegeneratingArticleOnlyLevel(level) ||
      isRegeneratingLevel(level) ||
      !article
    )
      return;

    setRegeneratingArticleOnlyLevels(prev => new Set(prev).add(level));

    try {
      const ttsRegenerateOptions =
        article.articleType === 'dialogue'
          ? {
              dialogueVoiceGenders: {
                A: dialogueVoiceGenders[level].A,
                B: dialogueVoiceGenders[level].B,
              },
            }
          : {
              voiceGender: monologueVoiceGenders[level],
            };

      const updated = await articleAdminApi.regenerateArticleOnlyTts(
        country || '',
        articleId,
        level,
        ttsRegenerateOptions
      );
      setArticle(updated);
      showNotification(`Article TTS level ${level} regenerated successfully`, 'success');
    } catch (error) {
      showNotification(
        error instanceof Error ? error.message : `Failed to regenerate article TTS level ${level}`,
        'error'
      );
    } finally {
      setRegeneratingArticleOnlyLevels(prev => {
        const next = new Set(prev);
        next.delete(level);
        return next;
      });
    }
  };

  const confirmRegenerateTtsAction = (state: NonNullable<TtsConfirmState>) => {
    setTtsConfirm(null);
    if (state.mode === 'articleOnly') {
      void handleRegenerateArticleOnlyTts(state.level);
      return;
    }
    void handleRegenerateTts(state.level);
  };

  const handleRegenerateLevelSentenceTts = async (level: TtsLevel) => {
    if (!articleId || !article || isRegeneratingSentenceLevel(level)) return;

    setRegeneratingSentenceLevels(prev => {
      const next = new Set(prev);
      next.add(level);
      return next;
    });

    try {
      const ttsRegenerateOptions =
        article.articleType === 'dialogue'
          ? {
              dialogueVoiceGenders: {
                A: dialogueVoiceGenders[level].A,
                B: dialogueVoiceGenders[level].B,
              },
            }
          : {
              voiceGender: monologueVoiceGenders[level],
            };

      const updated = await articleAdminApi.regenerateLevelSentenceTts(
        country || '',
        articleId,
        level,
        ttsRegenerateOptions
      );

      setArticle(updated);
      showNotification(`All sentence TTS in level ${level} regenerated successfully`, 'success');
    } catch (error) {
      showNotification(
        error instanceof Error
          ? error.message
          : `Failed to regenerate sentence TTS for level ${level}`,
        'error'
      );
    } finally {
      setRegeneratingSentenceLevels(prev => {
        const next = new Set(prev);
        next.delete(level);
        return next;
      });
    }
  };

  const isRegeneratingIpaLevel = (level: TtsLevel): boolean => regeneratingIpaLevels.has(level);

  const handleRegenerateIpa = async (level: TtsLevel) => {
    if (!articleId || isRegeneratingIpaLevel(level)) return;

    setRegeneratingIpaLevels(prev => new Set(prev).add(level));

    try {
      const updated = await articleAdminApi.regenerateIpa(country || '', articleId, level);
      setArticle(updated);
      showNotification(`IPA level ${level} regenerated successfully`, 'success');
    } catch (error) {
      showNotification(
        error instanceof Error ? error.message : `Failed to regenerate IPA level ${level}`,
        'error'
      );
    } finally {
      setRegeneratingIpaLevels(prev => {
        const next = new Set(prev);
        next.delete(level);
        return next;
      });
    }
  };

  const confirmRegenerateIpa = (level: TtsLevel) => {
    setIpaConfirm(null);
    void handleRegenerateIpa(level);
  };

  const handleRegenerateImage = async () => {
    if (!articleId || isRegeneratingImage) return;

    setIsRegeneratingImage(true);

    try {
      const updated = await articleAdminApi.regenerateImage(country || '', articleId);
      setArticle(updated);
      showNotification('Article image regenerated successfully', 'success');
    } catch (error) {
      showNotification(
        error instanceof Error ? error.message : 'Failed to regenerate article image',
        'error'
      );
    } finally {
      setIsRegeneratingImage(false);
    }
  };

  const confirmRegenerateImage = () => {
    setImageConfirm(null);
    void handleRegenerateImage();
  };

  const handleMarkAsPosted = async () => {
    if (!article || !articleId || isMarkingAsPosted || article.isFacebookPosted) {
      return;
    }

    setIsMarkingAsPosted(true);
    try {
      const updated = await articleAdminApi.update(country || '', articleId, {
        isFacebookPosted: true,
      });

      if (updated && typeof updated === 'object') {
        setArticle(updated as Article);
      } else {
        setArticle(prev => (prev ? { ...prev, isFacebookPosted: true } : prev));
      }

      showNotification('Marked as posted successfully', 'success');
    } catch (error) {
      showNotification('Failed to mark as posted', 'error');
    } finally {
      setIsMarkingAsPosted(false);
    }
  };

  const handleSubtitleLeadChange = (value: string) => {
    if (!value.trim()) {
      setSubtitleLeadSeconds(null);
      localStorage.removeItem(SUBTITLE_LEAD_STORAGE_KEY);
      return;
    }

    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return;
    }

    const clamped = Math.max(0, Math.min(2, parsed));
    setSubtitleLeadSeconds(clamped);
    localStorage.setItem(SUBTITLE_LEAD_STORAGE_KEY, String(clamped));
  };

  function splitIntoSentences(text = ''): string[] {
    if (!text) return [];
    // Prefer native sentence segmentation for better handling of abbreviations and decimals.
    const intlAny = Intl as any;
    if (typeof intlAny !== 'undefined' && typeof intlAny.Segmenter === 'function') {
      const segmenter = new intlAny.Segmenter('en', { granularity: 'sentence' });
      return (Array.from(segmenter.segment(text)) as Array<{ segment: string }>)
        .map(item => String(item.segment || '').trim())
        .filter(Boolean);
    }

    // Fallback for older environments.
    return (text.match(/[^.!?]+[.!?]*(?:\s+|$)/g) || []).map(s => s.trim()).filter(Boolean);
  }

  const tokenizeTranscript = (text: string): string[] => {
    if (!text) return [];
    return text.trim().split(/\s+/).filter(Boolean);
  };

  const supportsSpeechSynthesis =
    typeof window !== 'undefined' && typeof window.speechSynthesis !== 'undefined';

  const getSentenceAudioUrl = (level: TtsLevel, sentenceIndex: number): string | undefined => {
    const urls = isVi
      ? level === 'A'
        ? article?.ttsALevelSentenceUSSignedUrls
        : level === 'B'
          ? article?.ttsBLevelSentenceUSSignedUrls
          : article?.ttsCLevelSentenceUSSignedUrls
      : level === 'A'
        ? article?.ttsALevelSentenceENSignedUrls
        : level === 'B'
          ? article?.ttsBLevelSentenceENSignedUrls
          : article?.ttsCLevelSentenceENSignedUrls;

    if (!Array.isArray(urls) || sentenceIndex < 0 || sentenceIndex >= urls.length) {
      return undefined;
    }

    const raw = String(urls[sentenceIndex] || '').trim();
    return raw || undefined;
  };

  const handlePlaySentenceAudio = async (
    level: TtsLevel,
    sentenceIndex: number,
    sentenceText: string
  ) => {
    const sentenceKey = `${level}:${sentenceIndex}`;
    const sentenceAudioUrl = getSentenceAudioUrl(level, sentenceIndex);

    if (sentenceAudioUrl) {
      try {
        if (supportsSpeechSynthesis) {
          window.speechSynthesis.cancel();
        }

        if (sentenceAudioRef.current) {
          sentenceAudioRef.current.pause();
          sentenceAudioRef.current.src = '';
        }

        const audio = new Audio(sentenceAudioUrl);
        sentenceAudioRef.current = audio;
        setPlayingSentenceKey(sentenceKey);

        audio.onended = () => {
          setPlayingSentenceKey(current => (current === sentenceKey ? null : current));
        };
        audio.onerror = () => {
          setPlayingSentenceKey(current => (current === sentenceKey ? null : current));
        };

        await audio.play();
        return;
      } catch {
        setPlayingSentenceKey(current => (current === sentenceKey ? null : current));
      }
    }

    if (!supportsSpeechSynthesis) {
      showNotification('No sentence audio available for this line', 'info');
      return;
    }

    const normalizedText = String(sentenceText || '').trim();
    if (!normalizedText) {
      showNotification('No sentence audio available for this line', 'info');
      return;
    }

    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(normalizedText);
    utterance.onstart = () => {
      setPlayingSentenceKey(sentenceKey);
    };
    utterance.onend = () => {
      setPlayingSentenceKey(current => (current === sentenceKey ? null : current));
    };
    utterance.onerror = () => {
      setPlayingSentenceKey(current => (current === sentenceKey ? null : current));
    };
    window.speechSynthesis.speak(utterance);
  };

  const getWordBoundsByLine = (
    context: CanvasRenderingContext2D,
    words: string[],
    maxWidth: number
  ): string[][] => {
    const lines: string[][] = [];
    let currentLine: string[] = [];

    const splitLongWord = (word: string): string[] => {
      if (!word) return [];
      if (context.measureText(word).width <= maxWidth) return [word];

      const parts: string[] = [];
      let remaining = word;

      while (remaining.length > 0) {
        let end = remaining.length;
        while (end > 1 && context.measureText(remaining.slice(0, end)).width > maxWidth) {
          end -= 1;
        }

        const part = remaining.slice(0, end);
        parts.push(part);
        remaining = remaining.slice(end);
      }

      return parts;
    };

    for (const word of words) {
      const wordParts = splitLongWord(word);
      for (const part of wordParts) {
        const testLine = currentLine.length > 0 ? [...currentLine, part] : [part];
        const testText = testLine.join(' ');
        const measuredWidth = context.measureText(testText).width;

        if (measuredWidth <= maxWidth || currentLine.length === 0) {
          currentLine = testLine;
        } else {
          lines.push(currentLine);
          currentLine = [part];
        }
      }
    }

    if (currentLine.length > 0) {
      lines.push(currentLine);
    }

    return lines;
  };

  const resolveVideoFilename = (level: TtsLevel, aspect: VideoAspect) => {
    const toSlug = (value: string, toLower = true): string => {
      const normalized = toLower ? value.toLowerCase() : value;
      return normalized.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    };

    const countryPrefix = toSlug(String(country || 'TOPIC').toUpperCase(), false) || 'TOPIC';
    const categoryPrefix = toSlug(String(article?.category || 'general'), true) || 'general';
    const prefix = `${countryPrefix}-${categoryPrefix}`;

    const title = toSlug(
      String(article?.title || article?.titleUS || articleId || 'article'),
      true
    ).slice(0, 80);

    const orientation = aspect === '9x16' ? 'portrait' : 'landscape';
    return `${prefix}-${title || 'article'}-${level.toLowerCase()}-level-${orientation}-${aspect}.webm`;
  };

  const sanitizeDialogueTranscript = (text: string): string => {
    if (!text) return '';
    return text
      .replace(/^\s*\[(A|B)\]:\s*/gim, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  const getLevelMedia = (
    level: TtsLevel
  ): {
    audioUrl?: string;
    transcript: string;
    ipaLines: string[];
    translationLines: string[];
  } => {
    const audioMap: Record<TtsLevel, string | undefined> = isVi
      ? {
          A: article?.ttsALevelUSSignedUrl,
          B: article?.ttsBLevelUSSignedUrl,
          C: article?.ttsCLevelUSSignedUrl,
        }
      : {
          A: article?.ttsALevelENSignedUrl,
          B: article?.ttsBLevelENSignedUrl,
          C: article?.ttsCLevelENSignedUrl,
        };

    const transcriptMap: Record<TtsLevel, string> = isVi
      ? {
          A: String(article?.textALevelUS || ''),
          B: String(article?.textBLevelUS || ''),
          C: String(article?.textCLevelUS || ''),
        }
      : {
          A: String(article?.textALevelEN || ''),
          B: String(article?.textBLevelEN || ''),
          C: String(article?.textCLevelEN || ''),
        };

    const rawTranscript = transcriptMap[level] || '';
    const translationRawMap: Record<TtsLevel, string> = {
      A: String(article?.translatedTextALevel || article?.textALevelVI || ''),
      B: String(article?.translatedTextBLevel || article?.textBLevelVI || ''),
      C: String(article?.translatedTextCLevel || article?.textCLevelVI || ''),
    };
    const ipaRawMap: Record<TtsLevel, unknown> = isVi
      ? {
          A: (article as any)?.ipaALevelUS,
          B: (article as any)?.ipaBLevelUS,
          C: (article as any)?.ipaCLevelUS,
        }
      : {
          A: (article as any)?.ipaALevelEN,
          B: (article as any)?.ipaBLevelEN,
          C: (article as any)?.ipaCLevelEN,
        };

    const normalizeIpa = (field: unknown): string[] => {
      if (!field) return [];
      if (Array.isArray(field)) return field.map(item => String(item || '').trim());
      if (typeof field === 'string') return splitIntoSentences(field);
      return [];
    };

    const transcript =
      article?.articleType === 'dialogue'
        ? sanitizeDialogueTranscript(rawTranscript)
        : rawTranscript.trim();
    const ipaLines = normalizeIpa(ipaRawMap[level]);
    const translationLines = splitIntoSentences(translationRawMap[level]);

    return {
      audioUrl: audioMap[level],
      transcript,
      ipaLines,
      translationLines,
    };
  };

  const getMediaProxyUrl = (rawUrl: string): string => {
    const apiBase = String(import.meta.env.VITE_API_URL || 'http://localhost:3000/api').replace(
      /\/$/,
      ''
    );
    return `${apiBase}/admin/articles/proxy-media?url=${encodeURIComponent(rawUrl)}`;
  };

  const getAuthHeaders = (): HeadersInit => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return {};
    }
    return { Authorization: `Bearer ${token}` };
  };

  const handleCreateVideo = async (level: TtsLevel, aspect: VideoAspect) => {
    if (!article) {
      showNotification('Article details are not loaded yet', 'error');
      return;
    }

    const imageUrl = article.articleImageSignedUrl;
    const { audioUrl, transcript, ipaLines, translationLines } = getLevelMedia(level);

    if (!imageUrl) {
      showNotification('Missing article image for video background', 'error');
      return;
    }
    if (!audioUrl) {
      showNotification(`Missing level ${level} TTS audio for video soundtrack`, 'error');
      return;
    }
    if (!transcript) {
      showNotification(`Missing level ${level} text for transcript overlay`, 'error');
      return;
    }
    if (typeof MediaRecorder === 'undefined') {
      showNotification('Your browser does not support video recording on this page', 'error');
      return;
    }

    setIsGeneratingVideo(true);
    setGeneratingVideoLevel(level);
    setGeneratingVideoAspect(aspect);
    setVideoProgressText('Preparing media...');

    const videoWidth = aspect === '9x16' ? 720 : 1280;
    const videoHeight = aspect === '9x16' ? 1280 : 720;
    const sentences = splitIntoSentences(transcript);
    const articleTitle = String(article.title || article.titleUS || 'Untitled Article').trim();
    const customVideoTitle = String(videoTitleInputs[level] || '').trim();
    const articleViTitle = String(customVideoTitle || article.titleVI || '').trim();
    const countryCode = String(country || '')
      .trim()
      .toUpperCase();
    const titleCategory = String(article.category || '').trim() || 'Unknown';
    const prefix = `${countryCode || 'TOPIC'}-${titleCategory}`;

    let imageObjectUrl: string | null = null;
    let audioObjectUrl: string | null = null;
    let animationFrameId = 0;
    let progressIntervalId: number | null = null;
    let stopRecordingTimeoutId: number | null = null;
    let activeAudioContext: AudioContext | undefined;
    let activeAudioElement: HTMLAudioElement | undefined;
    let activeCanvasStream: MediaStream | undefined;
    let activeDestinationStream: MediaStream | undefined;

    const fetchWithTimeout = async (
      url: string,
      options: RequestInit,
      timeoutMs: number,
      timeoutMessage: string
    ): Promise<Response> => {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
      try {
        return await fetch(url, { ...options, signal: controller.signal });
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          throw new Error(timeoutMessage);
        }
        throw error;
      } finally {
        window.clearTimeout(timeoutId);
      }
    };

    const readBlobWithTimeout = async (
      response: Response,
      timeoutMs: number,
      timeoutMessage: string
    ): Promise<Blob> => {
      return await new Promise<Blob>((resolve, reject) => {
        let settled = false;

        const timeoutId = window.setTimeout(() => {
          if (settled) return;
          settled = true;
          reject(new Error(timeoutMessage));
        }, timeoutMs);

        response
          .blob()
          .then(blob => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timeoutId);
            resolve(blob);
          })
          .catch(error => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timeoutId);
            reject(error instanceof Error ? error : new Error('Failed to read media blob'));
          });
      });
    };

    const cleanup = () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
      if (progressIntervalId) {
        window.clearInterval(progressIntervalId);
      }
      if (stopRecordingTimeoutId) {
        window.clearTimeout(stopRecordingTimeoutId);
      }
      if (activeAudioElement) {
        activeAudioElement.pause();
        activeAudioElement.src = '';
      }
      if (activeCanvasStream) {
        activeCanvasStream.getTracks().forEach(track => track.stop());
      }
      if (activeDestinationStream) {
        activeDestinationStream.getTracks().forEach(track => track.stop());
      }
      if (activeAudioContext) {
        void activeAudioContext.close();
      }
      if (imageObjectUrl) {
        URL.revokeObjectURL(imageObjectUrl);
      }
      if (audioObjectUrl) {
        URL.revokeObjectURL(audioObjectUrl);
      }
      setVideoProgressText('');
      setGeneratingVideoLevel(null);
      setGeneratingVideoAspect(null);
      setIsGeneratingVideo(false);
    };

    try {
      setVideoProgressText('Loading background image...');
      const imageResponse = await fetchWithTimeout(
        getMediaProxyUrl(imageUrl),
        { headers: getAuthHeaders() },
        15000,
        'Timed out while loading article image'
      );
      if (!imageResponse.ok) {
        throw new Error('Failed to load article image');
      }
      const imageBlob = await readBlobWithTimeout(
        imageResponse,
        15000,
        'Timed out while processing article image'
      );
      imageObjectUrl = URL.createObjectURL(imageBlob);

      const backgroundImage = await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Cannot decode article image'));
        img.src = imageObjectUrl as string;
      });

      setVideoProgressText(`Loading level ${level} TTS audio...`);
      const audioResponse = await fetchWithTimeout(
        getMediaProxyUrl(audioUrl),
        { headers: getAuthHeaders() },
        20000,
        `Timed out while loading level ${level} TTS audio`
      );
      if (!audioResponse.ok) {
        throw new Error(`Failed to load level ${level} TTS audio`);
      }
      const audioBlob = await readBlobWithTimeout(
        audioResponse,
        30000,
        `Timed out while processing level ${level} TTS audio`
      );
      audioObjectUrl = URL.createObjectURL(audioBlob);

      const canvas = document.createElement('canvas');
      canvas.width = videoWidth;
      canvas.height = videoHeight;
      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('Cannot initialize canvas context');
      }

      const audioElement = new Audio(audioObjectUrl);
      audioElement.preload = 'auto';
      activeAudioElement = audioElement;

      await new Promise<void>((resolve, reject) => {
        const onLoaded = () => {
          cleanupListeners();
          resolve();
        };
        const onError = () => {
          cleanupListeners();
          reject(new Error(`Cannot decode level ${level} TTS audio`));
        };
        const cleanupListeners = () => {
          audioElement.removeEventListener('loadedmetadata', onLoaded);
          audioElement.removeEventListener('error', onError);
        };

        audioElement.addEventListener('loadedmetadata', onLoaded);
        audioElement.addEventListener('error', onError);
      });

      const duration = audioElement.duration;
      if (!Number.isFinite(duration) || duration <= 0) {
        throw new Error(`Invalid level ${level} audio duration`);
      }

      const canvasStream = canvas.captureStream(30);
      const audioContext = new AudioContext();
      activeCanvasStream = canvasStream;
      activeAudioContext = audioContext;
      const sourceNode = audioContext.createMediaElementSource(audioElement);
      const destinationNode = audioContext.createMediaStreamDestination();
      activeDestinationStream = destinationNode.stream;
      sourceNode.connect(destinationNode);

      const mixedStream = new MediaStream([
        ...canvasStream.getVideoTracks(),
        ...destinationNode.stream.getAudioTracks(),
      ]);

      const supportedMimeType = [
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm',
      ].find(type => MediaRecorder.isTypeSupported(type));

      const recorder = supportedMimeType
        ? new MediaRecorder(mixedStream, { mimeType: supportedMimeType })
        : new MediaRecorder(mixedStream);

      const chunks: BlobPart[] = [];
      recorder.ondataavailable = event => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      // Pre-compute title text and wrapped transcript/IPA lines once (not per frame).
      const layout =
        aspect === '9x16'
          ? {
              captionPanelPadding: 32,
              englishFont: '600 30px "Segoe UI", sans-serif',
              ipaFont: '500 20px "Segoe UI", sans-serif',
              translationFont: '500 18px "Segoe UI", sans-serif',
              transcriptLineHeight: 30,
              panelY: videoHeight - 570,
              panelHeight: 360,
              topBandHeight: 220,
              endCardTitleWidthPadding: 70,
              endCardTitleY: videoHeight / 2,
              titleLineHeight: 30,
              panelTextStartY: 74,
            }
          : {
              captionPanelPadding: 44,
              englishFont: '600 34px "Segoe UI", sans-serif',
              ipaFont: '500 24px "Segoe UI", sans-serif',
              translationFont: '500 22px "Segoe UI", sans-serif',
              transcriptLineHeight: 32,
              panelY: videoHeight - 330,
              panelHeight: 330,
              topBandHeight: 140,
              endCardTitleWidthPadding: 120,
              endCardTitleY: videoHeight / 2,
              titleLineHeight: 36,
              panelTextStartY: 74,
            };

      const captionPanelPadding = layout.captionPanelPadding;
      const captionPanelWidth = videoWidth - captionPanelPadding * 2;
      const englishFont = layout.englishFont;
      const ipaFont = layout.ipaFont;
      const translationFont = layout.translationFont;
      const transcriptLineHeight = layout.transcriptLineHeight;
      const customTitleFont = '700 28px "Segoe UI", sans-serif';
      const customTitleLineHeight = 34;
      const endCardCtaFont = customTitleFont;
      const endCardDurationMs = 5000;
      let endCardStartedAt: number | null = null;
      const endCardCtaLines = [
        'Practice vocabulary, shadowing, dictation',
        'and translating this article for free',
        'at VicaTalk.com',
        'Luyện tập từ vựng, shadowing, dictation, và dịch',
        'bài này miễn phí tại VicaTalk.com',
      ];
      const topTitleFont =
        aspect === '9x16' ? '700 30px "Segoe UI", sans-serif' : '700 34px "Segoe UI", sans-serif';
      const topSubtitleFont =
        aspect === '9x16' ? '600 24px "Segoe UI", sans-serif' : '600 28px "Segoe UI", sans-serif';
      const topTitleLineHeight = aspect === '9x16' ? 34 : 38;
      const topSubtitleLineHeight = aspect === '9x16' ? 28 : 32;
      const topTitleMaxWidth = Math.min(videoWidth - 80, aspect === '9x16' ? 620 : 1080);

      context.font = customTitleFont;
      const preparedTitle = `[${prefix}] ${articleTitle} - ${getLevelLabel(level)} Level`;
      const titleMaxWidth =
        aspect === '9x16'
          ? Math.min(videoWidth - layout.endCardTitleWidthPadding * 2, 560)
          : Math.min(videoWidth - layout.endCardTitleWidthPadding * 2, 900);
      let titleLines = getWordBoundsByLine(
        context,
        tokenizeTranscript(preparedTitle),
        titleMaxWidth
      ).map(line => line.join(' '));

      if (titleLines.length === 1 && preparedTitle.length > 42) {
        const words = tokenizeTranscript(preparedTitle);
        let bestSplitIndex = -1;
        let bestScore = Number.POSITIVE_INFINITY;

        for (let splitIndex = 1; splitIndex < words.length; splitIndex += 1) {
          const firstLine = words.slice(0, splitIndex).join(' ');
          const secondLine = words.slice(splitIndex).join(' ');
          const firstWidth = context.measureText(firstLine).width;
          const secondWidth = context.measureText(secondLine).width;
          const overflowPenalty =
            Math.max(0, firstWidth - titleMaxWidth) + Math.max(0, secondWidth - titleMaxWidth);
          const balancePenalty = Math.abs(firstWidth - secondWidth);
          const score = overflowPenalty * 1000 + balancePenalty;

          if (score < bestScore) {
            bestScore = score;
            bestSplitIndex = splitIndex;
          }
        }

        if (bestSplitIndex > 0) {
          titleLines = [
            words.slice(0, bestSplitIndex).join(' '),
            words.slice(bestSplitIndex).join(' '),
          ];
        }
      }

      context.font = topTitleFont;
      const topTitleLines = getWordBoundsByLine(
        context,
        tokenizeTranscript(articleTitle),
        topTitleMaxWidth
      )
        .map(line => line.join(' '))
        .slice(0, 2);

      let topSubtitleLines: string[] = [];
      if (articleViTitle) {
        context.font = topSubtitleFont;
        topSubtitleLines = getWordBoundsByLine(
          context,
          tokenizeTranscript(articleViTitle),
          topTitleMaxWidth
        )
          .map(line => line.join(' '))
          .slice(0, 2);
      }

      const panelTextStartY = Math.max(24, layout.panelTextStartY - transcriptLineHeight);

      context.font = englishFont;
      const sentenceBlocks: string[][] = sentences.map(s =>
        getWordBoundsByLine(context, tokenizeTranscript(s), captionPanelWidth - 24).map(line =>
          line.join(' ')
        )
      );

      context.font = ipaFont;
      const ipaBlocks: string[][] = sentences.map((_, sentenceIdx) => {
        const ipaText = String(ipaLines[sentenceIdx] || '').trim();
        if (!ipaText) return [];
        return getWordBoundsByLine(
          context,
          tokenizeTranscript(ipaText),
          captionPanelWidth - 24
        ).map(line => line.join(' '));
      });

      context.font = translationFont;
      const translationBlocks: string[][] = sentences.map((_, sentenceIdx) => {
        const translationText = String(translationLines[sentenceIdx] || '').trim();
        if (!translationText) return [];
        return getWordBoundsByLine(
          context,
          tokenizeTranscript(translationText),
          captionPanelWidth - 24
        ).map(line => line.join(' '));
      });

      // Show subtitles slightly ahead of audio to compensate rendering/playback delay.
      const fallbackSubtitleLeadSeconds = article?.articleType === 'dialogue' ? 0.45 : 0.25;
      const effectiveSubtitleLeadSeconds = subtitleLeadSeconds ?? fallbackSubtitleLeadSeconds;

      // Pre-compute cumulative time boundaries from speech-oriented weights.
      // Word count tracks pacing more reliably than character length.
      const sentenceBoundaries: number[] = ((): number[] => {
        if (sentences.length === 0) return [];

        const sentenceWeights = sentences.map(sentence => {
          const cleaned = sentence
            .toLowerCase()
            .replace(/[^a-z0-9\s']/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
          const wordCount = cleaned ? cleaned.split(' ').filter(Boolean).length : 0;
          return Math.max(wordCount, 1);
        });

        const totalWeight = sentenceWeights.reduce((sum, weight) => sum + weight, 0);
        const normalizedDurations =
          totalWeight > 0
            ? sentenceWeights.map(weight => (weight / totalWeight) * duration)
            : sentenceWeights.map(() => duration / sentences.length);

        let cumulative = 0;
        return normalizedDurations.map(item => {
          cumulative += item / Math.max(duration, 0.0001);
          return cumulative;
        });
      })();

      const drawFrame = () => {
        context.clearRect(0, 0, videoWidth, videoHeight);

        context.fillStyle = '#111827';
        context.fillRect(0, 0, videoWidth, videoHeight);

        // Keep full image visible inside the selected output aspect ratio.
        const imageScale = Math.min(
          videoWidth / backgroundImage.width,
          videoHeight / backgroundImage.height
        );
        const imageDrawWidth = backgroundImage.width * imageScale;
        const imageDrawHeight = backgroundImage.height * imageScale;
        const imageX = (videoWidth - imageDrawWidth) / 2;
        const imageY = (videoHeight - imageDrawHeight) / 2;

        context.drawImage(backgroundImage, imageX, imageY, imageDrawWidth, imageDrawHeight);

        const gradient = context.createLinearGradient(0, 0, 0, videoHeight);
        gradient.addColorStop(0, 'rgba(0, 0, 0, 0.15)');
        gradient.addColorStop(0.6, 'rgba(0, 0, 0, 0.45)');
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0.78)');
        context.fillStyle = gradient;
        context.fillRect(0, 0, videoWidth, videoHeight);

        if (endCardStartedAt !== null) {
          context.fillStyle = 'rgba(0, 0, 0, 0.68)';
          context.fillRect(0, 0, videoWidth, videoHeight);

          const ctaLineHeight = customTitleLineHeight;
          const ctaToTitleSpacing = 18;
          const ctaBlockHeight = endCardCtaLines.length * ctaLineHeight;
          const titleBlockHeight = titleLines.length * layout.titleLineHeight;
          const totalBlockHeight = ctaBlockHeight + ctaToTitleSpacing + titleBlockHeight;
          const blockStartY = layout.endCardTitleY - totalBlockHeight / 2;
          const titleStartY = blockStartY + ctaBlockHeight + ctaToTitleSpacing;

          context.textAlign = 'center';
          context.font = endCardCtaFont;
          context.fillStyle = 'rgba(255, 255, 255, 0.96)';
          endCardCtaLines.forEach((line, i) => {
            context.fillText(line, videoWidth / 2, blockStartY + (i + 1) * ctaLineHeight);
          });

          context.font = customTitleFont;
          context.fillStyle = '#f8fafc';
          titleLines.forEach((line, i) => {
            context.fillText(line, videoWidth / 2, titleStartY + (i + 1) * layout.titleLineHeight);
          });

          animationFrameId = window.requestAnimationFrame(drawFrame);
          return;
        }

        context.fillStyle = 'rgba(0, 0, 0, 0.2)';
        context.fillRect(0, 0, videoWidth, layout.topBandHeight);

        context.textAlign = 'center';
        context.textBaseline = 'middle';
        const topBlockStartY = 300;
        const topBlockHeight =
          topTitleLines.length * topTitleLineHeight +
          topSubtitleLines.length * topSubtitleLineHeight +
          (topTitleLines.length > 0 && topSubtitleLines.length > 0 ? 10 : 0);
        const topPanelPaddingY = 16;
        const topPanelY = topBlockStartY - topPanelPaddingY;
        const topPanelHeight = topBlockHeight + topPanelPaddingY * 2;
        const topPanelX = captionPanelPadding;
        const topPanelWidth = videoWidth - captionPanelPadding * 2;
        context.fillStyle = 'rgba(0, 0, 0, 0.62)';
        context.fillRect(topPanelX, topPanelY, topPanelWidth, topPanelHeight);
        let currentTopLineY = topBlockStartY;

        context.fillStyle = '#facc15';
        context.font = topTitleFont;
        topTitleLines.forEach(line => {
          context.fillText(line, videoWidth / 2, currentTopLineY + topTitleLineHeight / 2);
          currentTopLineY += topTitleLineHeight;
        });

        if (topSubtitleLines.length > 0) {
          currentTopLineY += topTitleLines.length > 0 ? 10 : 0;
          context.font = topSubtitleFont;
          context.fillStyle = '#86efac';
          topSubtitleLines.forEach(line => {
            context.fillText(line, videoWidth / 2, currentTopLineY + topSubtitleLineHeight / 2);
            currentTopLineY += topSubtitleLineHeight;
          });
        }

        const progress = (() => {
          if (!Number.isFinite(duration) || duration <= 0) return 0;
          const adjustedTime = Math.min(
            duration,
            Math.max(0, audioElement.currentTime + effectiveSubtitleLeadSeconds)
          );
          return adjustedTime / duration;
        })();
        const currentSentenceIndex = (() => {
          if (sentences.length === 0) return 0;
          const idx = sentenceBoundaries.findIndex(boundary => progress < boundary);
          return idx === -1 ? sentences.length - 1 : idx;
        })();

        const panelPadding = captionPanelPadding;
        const panelWidth = videoWidth - panelPadding * 2;
        const panelX = panelPadding;
        const panelY = layout.panelY;
        const panelHeight = layout.panelHeight;

        const watermarkY = Math.max(
          layout.topBandHeight + 40,
          panelY - (aspect === '9x16' ? 34 : 26)
        );
        context.textAlign = 'center';
        context.textBaseline = 'alphabetic';
        context.font =
          aspect === '9x16' ? '700 34px "Segoe UI", sans-serif' : '700 30px "Segoe UI", sans-serif';
        context.fillStyle = 'rgba(239, 68, 68, 0.78)';
        context.fillText('VicaTalk.com', videoWidth / 2, watermarkY);

        context.fillStyle = 'rgba(0, 0, 0, 0.62)';
        context.fillRect(panelX, panelY, panelWidth, panelHeight);
        context.textAlign = 'center';

        // Build display rows: only the current sentence (English + IPA + translation).
        const displayRows: {
          text: string;
          isCurrent: boolean;
          kind: 'en' | 'ipa' | 'translation';
        }[] = [];
        for (const line of sentenceBlocks[currentSentenceIndex] ?? []) {
          displayRows.push({ text: line, isCurrent: true, kind: 'en' });
        }
        for (const ipaLine of ipaBlocks[currentSentenceIndex] ?? []) {
          displayRows.push({ text: ipaLine, isCurrent: true, kind: 'ipa' });
        }
        for (const translationLine of translationBlocks[currentSentenceIndex] ?? []) {
          displayRows.push({ text: translationLine, isCurrent: true, kind: 'translation' });
        }

        const availableTextHeight = Math.max(40, panelHeight - panelTextStartY - 12);
        const needsCompactLayout = displayRows.length * transcriptLineHeight > availableTextHeight;
        const effectiveLineHeight = needsCompactLayout
          ? Math.max(16, Math.floor(availableTextHeight / Math.max(displayRows.length, 1)))
          : transcriptLineHeight;

        const compactEnglishFont =
          aspect === '9x16' ? '600 24px "Segoe UI", sans-serif' : '600 22px "Segoe UI", sans-serif';
        const compactIpaFont =
          aspect === '9x16' ? '500 18px "Segoe UI", sans-serif' : '500 17px "Segoe UI", sans-serif';
        const compactTranslationFont =
          aspect === '9x16' ? '500 16px "Segoe UI", sans-serif' : '500 15px "Segoe UI", sans-serif';

        context.textAlign = 'center';
        displayRows.forEach((lineInfo, lineIdx) => {
          const lineY = panelY + panelTextStartY + lineIdx * effectiveLineHeight;
          if (lineInfo.kind === 'ipa') {
            context.font = needsCompactLayout ? compactIpaFont : ipaFont;
            context.fillStyle = lineInfo.isCurrent ? '#93c5fd' : 'rgba(191, 219, 254, 0.7)';
          } else if (lineInfo.kind === 'translation') {
            context.font = needsCompactLayout ? compactTranslationFont : translationFont;
            context.fillStyle = lineInfo.isCurrent ? '#86efac' : 'rgba(187, 247, 208, 0.7)';
          } else {
            context.font = needsCompactLayout ? compactEnglishFont : englishFont;
            context.fillStyle = lineInfo.isCurrent ? '#facc15' : 'rgba(255, 255, 255, 0.6)';
          }
          context.fillText(lineInfo.text, videoWidth / 2, lineY);
        });

        animationFrameId = window.requestAnimationFrame(drawFrame);
      };

      await audioContext.resume();
      setVideoProgressText(`Recording ${aspect} video...`);

      drawFrame();
      recorder.start(250);

      progressIntervalId = window.setInterval(() => {
        const current = Number.isFinite(audioElement.currentTime) ? audioElement.currentTime : 0;
        const percent = Math.min(100, Math.round((current / duration) * 100));
        setVideoProgressText(`Recording ${aspect} video... ${percent}%`);
      }, 250);

      await audioElement.play();

      await new Promise<void>((resolve, reject) => {
        recorder.onstop = () => {
          cleanupListeners();
          resolve();
        };
        recorder.onerror = () => {
          cleanupListeners();
          reject(new Error('Video recorder failed while finalizing output'));
        };

        const onEnded = () => {
          endCardStartedAt = performance.now();
          setVideoProgressText(`Finalizing ${aspect} video...`);
          stopRecordingTimeoutId = window.setTimeout(() => {
            recorder.stop();
          }, endCardDurationMs);
        };
        const onError = () => {
          cleanupListeners();
          reject(new Error('Audio playback failed during recording'));
        };
        const cleanupListeners = () => {
          audioElement.removeEventListener('ended', onEnded);
          audioElement.removeEventListener('error', onError);
        };

        audioElement.addEventListener('ended', onEnded);
        audioElement.addEventListener('error', onError);
      });

      const videoBlob = new Blob(chunks, { type: supportedMimeType || 'video/webm' });
      const downloadUrl = URL.createObjectURL(videoBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = resolveVideoFilename(level, aspect);
      link.click();
      URL.revokeObjectURL(downloadUrl);

      showNotification(
        `Level ${level} ${aspect} video created successfully. Download started.`,
        'success',
        4500
      );
      cleanup();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create video';
      showNotification(
        `${aspect} video creation failed. Make sure image/audio URLs are accessible. (${message})`,
        'error',
        5000
      );
      cleanup();
    }
  };

  function normalizeIpaField(field: any): string[] {
    if (!field) return [];
    if (Array.isArray(field)) return field.map(String);
    if (typeof field === 'string') return splitIntoSentences(field);
    return [];
  }

  const isVi = !country || country?.toUpperCase() === 'VI';
  const artAny = article as any;

  // Non-VI countries: single text per level
  let aSentences: string[] = [];
  let bSentences: string[] = [];
  let cSentences: string[] = [];
  let ipaA: Record<string, string[]> = {};
  let ipaB: Record<string, string[]> = {};
  let ipaC: Record<string, string[]> = {};

  // VI: US variant only
  let aUsSentences: string[] = [];
  let bUsSentences: string[] = [];
  let cUsSentences: string[] = [];
  let ipaUsA: string[] = [];
  let ipaUsB: string[] = [];
  let ipaUsC: string[] = [];

  if (!isVi) {
    aSentences = splitIntoSentences(article?.textALevelEN ?? '');
    bSentences = splitIntoSentences(article?.textBLevelEN ?? '');
    cSentences = splitIntoSentences(article?.textCLevelEN ?? '');
    ipaA.en = normalizeIpaField(artAny?.ipaALevelEN);
    ipaB.en = normalizeIpaField(artAny?.ipaBLevelEN);
    ipaC.en = normalizeIpaField(artAny?.ipaCLevelEN);
  } else {
    aUsSentences = splitIntoSentences(article?.textALevelUS ?? '');
    bUsSentences = splitIntoSentences(article?.textBLevelUS ?? '');
    cUsSentences = splitIntoSentences(article?.textCLevelUS ?? '');
    ipaUsA = normalizeIpaField(artAny?.ipaALevelUS);
    ipaUsB = normalizeIpaField(artAny?.ipaBLevelUS);
    ipaUsC = normalizeIpaField(artAny?.ipaCLevelUS);
  }

  const [ipaAState, setIpaAState] = useState<Record<string, string[]>>({});
  const [ipaBState, setIpaBState] = useState<Record<string, string[]>>({});
  const [ipaCState, setIpaCState] = useState<Record<string, string[]>>({});
  const containerRef = useRef<HTMLFormElement | null>(null);

  // Initialize editable IPA arrays when article changes
  useEffect(() => {
    if (!isVi) {
      const aInit = ipaA.en?.slice(0, aSentences.length) ?? [];
      while (aInit.length < aSentences.length) aInit.push('');
      setIpaAState({ en: aInit });

      const bInit = ipaB.en?.slice(0, bSentences.length) ?? [];
      while (bInit.length < bSentences.length) bInit.push('');
      setIpaBState({ en: bInit });

      const cInit = ipaC.en?.slice(0, cSentences.length) ?? [];
      while (cInit.length < cSentences.length) cInit.push('');
      setIpaCState({ en: cInit });
    } else {
      const aUsInit = ipaUsA.slice(0, aUsSentences.length);
      while (aUsInit.length < aUsSentences.length) aUsInit.push('');
      setIpaAState({ us: aUsInit });

      const bUsInit = ipaUsB.slice(0, bUsSentences.length);
      while (bUsInit.length < bUsSentences.length) bUsInit.push('');
      setIpaBState({ us: bUsInit });

      const cUsInit = ipaUsC.slice(0, cUsSentences.length);
      while (cUsInit.length < cUsSentences.length) cUsInit.push('');
      setIpaCState({ us: cUsInit });
    }
  }, [
    article?.articleId,
    artAny?.updatedAt,
    isVi,
    aSentences.length,
    bSentences.length,
    cSentences.length,
    aUsSentences.length,
    bUsSentences.length,
    cUsSentences.length,
  ]);

  // Auto-size textareas after render when content/state changes
  useLayoutEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const areas = root.querySelectorAll('textarea');
    areas.forEach(a => {
      const ta = a as HTMLTextAreaElement;
      ta.style.height = 'auto';
      ta.style.height = ta.scrollHeight + 'px';
    });
  }, [
    ipaAState,
    ipaBState,
    ipaCState,
    aSentences.length,
    bSentences.length,
    cSentences.length,
    aUsSentences.length,
    bUsSentences.length,
    cUsSentences.length,
  ]);

  const countryCode = String(country || '')
    .trim()
    .toUpperCase();
  const isDialogue = article?.articleType === 'dialogue';
  const isNewsCategory =
    String(article?.category || '')
      .trim()
      .toLowerCase() === 'news';
  const englishPrefix =
    countryCode === 'US'
      ? 'Learn American English'
      : countryCode === 'AU'
        ? 'Learn Australian English'
        : 'Learn English';
  const vietnamesePrefix =
    countryCode === 'US'
      ? 'Học tiếng Anh Mỹ'
      : countryCode === 'AU'
        ? 'Học tiếng Anh Úc'
        : 'Học tiếng Anh';
  const englishSuffix = isDialogue
    ? ' through conversation: '
    : isNewsCategory
      ? ' through News: '
      : ' through article: ';
  const vietnameseSuffix = isDialogue
    ? ' qua hội thoại: '
    : isNewsCategory
      ? ' qua tin tức: '
      : ' qua bài: ';

  const englishLine = `${englishPrefix}${englishSuffix}${String(article?.title || article?.titleUS || '').trim()}`;
  const vietnameseLine = `${vietnamesePrefix}${vietnameseSuffix}${String(article?.titleVI || '').trim()}`;
  const newsTagCountryCode = countryCode === 'VI' ? 'VN' : countryCode || 'TOPIC';
  const tags: string[] = [];
  if (countryCode === 'US') tags.push('#AmericanEnglish');
  if (countryCode === 'AU') tags.push('#AustralianEnglish');
  if (isDialogue) tags.push('#EnglishConversation');
  if (isNewsCategory) tags.push(`#${newsTagCountryCode}News`);
  tags.push('#VicaTalk', '#learningEnglish', '#practiceSpeaking', '#esl', '#learnEnglish');
  const reelDescription = `${englishLine}\n\n${vietnameseLine}\n\n${tags.join(' ')}`;

  const renderTtsPlayer = (level: TtsLevel, src?: string, variantLabel?: string) => (
    <div className="space-y-2">
      <div className="space-y-1">
        <label className="block text-sm font-medium text-gray-700">Video title</label>
        <input
          type="text"
          value={videoTitleInputs[level] || ''}
          onChange={e =>
            setVideoTitleInputs(prev => ({
              ...prev,
              [level]: e.target.value,
            }))
          }
          placeholder={`Enter custom video title for level ${level} (optional)`}
          className="w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-900"
        />
      </div>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold text-gray-600">{variantLabel || '-'}</p>
        <div className="flex items-center gap-2 md:flex-none">
          <Button
            type="button"
            variant="outline"
            size="sm"
            loading={
              isGeneratingVideo &&
              generatingVideoLevel === level &&
              generatingVideoAspect === '9x16'
            }
            disabled={
              isLoading ||
              isGeneratingVideo ||
              regeneratingLevels.size > 0 ||
              regeneratingIpaLevels.size > 0 ||
              !article
            }
            onClick={() => handleCreateVideo(level, '9x16')}
          >
            Create Video 9x16
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            loading={
              isGeneratingVideo &&
              generatingVideoLevel === level &&
              generatingVideoAspect === '16x9'
            }
            disabled={
              isLoading ||
              isGeneratingVideo ||
              regeneratingLevels.size > 0 ||
              regeneratingIpaLevels.size > 0 ||
              !article
            }
            onClick={() => handleCreateVideo(level, '16x9')}
          >
            Create Video 16x9
          </Button>
        </div>
      </div>
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="min-w-0 flex-1">
          {src ? (
            <audio controls src={src} className="w-full" />
          ) : (
            <div className="text-sm text-gray-500">No audio available</div>
          )}
        </div>
        <div className="flex items-center gap-2 md:flex-none">
          {article?.articleType === 'dialogue' ? (
            <>
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-600 whitespace-nowrap">A turn</span>
                <select
                  value={dialogueVoiceGenders[level].A}
                  onChange={e => {
                    const value = e.target.value as VoiceGender;
                    setDialogueVoiceGenders(prev => ({
                      ...prev,
                      [level]: {
                        ...prev[level],
                        A: value,
                      },
                    }));
                  }}
                  disabled={isLoading || isRegeneratingImage || isRegeneratingLevel(level)}
                  className="rounded border border-gray-300 px-2 py-1 text-sm text-gray-900"
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-600 whitespace-nowrap">B turn</span>
                <select
                  value={dialogueVoiceGenders[level].B}
                  onChange={e => {
                    const value = e.target.value as VoiceGender;
                    setDialogueVoiceGenders(prev => ({
                      ...prev,
                      [level]: {
                        ...prev[level],
                        B: value,
                      },
                    }));
                  }}
                  disabled={
                    isLoading ||
                    isRegeneratingImage ||
                    isRegeneratingLevel(level) ||
                    isRegeneratingArticleOnlyLevel(level)
                  }
                  className="rounded border border-gray-300 px-2 py-1 text-sm text-gray-900"
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-600 whitespace-nowrap">Voice</span>
              <select
                value={monologueVoiceGenders[level]}
                onChange={e => {
                  const value = e.target.value as VoiceGender;
                  setMonologueVoiceGenders(prev => ({
                    ...prev,
                    [level]: value,
                  }));
                }}
                disabled={
                  isLoading ||
                  isRegeneratingImage ||
                  isRegeneratingLevel(level) ||
                  isRegeneratingArticleOnlyLevel(level)
                }
                className="rounded border border-gray-300 px-2 py-1 text-sm text-gray-900"
              >
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 md:flex-none">
          <Button
            type="button"
            variant="outline"
            size="sm"
            loading={isRegeneratingArticleOnlyLevel(level)}
            disabled={isLoading || isRegeneratingImage || isRegeneratingLevel(level) || !article}
            onClick={() => setTtsConfirm({ level, mode: 'articleOnly' })}
          >
            Re-generate article TTS
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            loading={isRegeneratingLevel(level)}
            disabled={
              isLoading || isRegeneratingImage || isRegeneratingArticleOnlyLevel(level) || !article
            }
            onClick={() => setTtsConfirm({ level, mode: 'full' })}
          >
            Re-generate article and sentence TTS
          </Button>
        </div>
      </div>
    </div>
  );

  const renderSentencePlayButton = (level: TtsLevel, sentenceIndex: number, sentence: string) => {
    const sentenceKey = `${level}:${sentenceIndex}`;
    const isPlaying = playingSentenceKey === sentenceKey;

    return (
      <button
        type="button"
        aria-label="Play sentence audio"
        title="Play sentence audio"
        className={`shrink-0 rounded p-1 transition-colors ${
          isPlaying
            ? 'text-primary-700 bg-primary-50'
            : 'text-primary-600 hover:bg-primary-50 hover:text-primary-700'
        }`}
        onClick={() => {
          void handlePlaySentenceAudio(level, sentenceIndex, sentence);
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M8.25 6.75A.75.75 0 0 1 9.5 6.2l7 5.25a.75.75 0 0 1 0 1.1l-7 5.25A.75.75 0 0 1 8.25 17.25v-10.5Z" />
        </svg>
      </button>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <Toast notification={notification} onClose={() => setNotification(null)} />
      <ConfirmModal
        open={showPublishConfirm}
        title="Confirm Approve TTS"
        description="Are you sure you want to approve this article TTS files?"
        confirmText="Yes, approve"
        cancelText="Cancel"
        isLoading={isLoading}
        onConfirm={performApprove}
        onCancel={() => setShowApproveConfirm(false)}
      />
      <ConfirmModal
        open={Boolean(ttsConfirm)}
        title={
          ttsConfirm?.mode === 'articleOnly'
            ? 'Confirm Re-generate Article TTS'
            : 'Confirm Re-generate Article and Sentence TTS'
        }
        description={
          ttsConfirm?.mode === 'articleOnly'
            ? `Are you sure you want to re-generate article TTS level ${ttsConfirm?.level || ''}? This will replace only the full-level audio file.`
            : `Are you sure you want to re-generate article and sentence TTS level ${ttsConfirm?.level || ''}? This will delete current level audio and sentence files from S3, then generate and upload new files.`
        }
        confirmText="Yes, re-generate"
        cancelText="Cancel"
        isLoading={false}
        onConfirm={() => {
          if (ttsConfirm) {
            confirmRegenerateTtsAction(ttsConfirm);
          }
        }}
        onCancel={() => setTtsConfirm(null)}
      />
      <ConfirmModal
        open={Boolean(ipaConfirm)}
        title="Confirm Re-generate IPA"
        description={`Are you sure you want to re-generate IPA level ${ipaConfirm?.level || ''}? This will replace all current IPA lines for this level.`}
        confirmText="Yes, re-generate"
        cancelText="Cancel"
        isLoading={false}
        onConfirm={() => {
          if (ipaConfirm) {
            confirmRegenerateIpa(ipaConfirm.level);
          }
        }}
        onCancel={() => setIpaConfirm(null)}
      />
      <ConfirmModal
        open={Boolean(imageConfirm)}
        title="Confirm Re-generate Image"
        description="Are you sure you want to re-generate the article image? If an image exists, it will be deleted from S3 first, then a new image will be generated, uploaded to S3, and saved to the database."
        confirmText="Yes, re-generate"
        cancelText="Cancel"
        isLoading={isRegeneratingImage}
        onConfirm={confirmRegenerateImage}
        onCancel={() => setImageConfirm(null)}
      />

      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Review TTS</h1>
          <p className="text-gray-600 mt-2">Review generated TTS audio for the article</p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate('/editor/articles', { state: { activeTab: 'tts' } })}
        >
          ← Back to Articles
        </Button>
      </div>

      {isGeneratingVideo && videoProgressText && (
        <p className="mb-4 text-sm text-blue-700">{videoProgressText}</p>
      )}

      <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 p-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Subtitle lead (seconds)
            </label>
            <p className="text-xs text-gray-500">
              Persisted locally and used for video highlight timing. Leave empty to use recommended
              values.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={0}
              max={2}
              step={0.05}
              value={subtitleLeadSeconds ?? ''}
              onChange={e => handleSubtitleLeadChange(e.target.value)}
              placeholder="Auto"
              className="w-28 rounded border border-gray-300 px-2 py-1 text-sm text-gray-900"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleSubtitleLeadChange('')}
            >
              Use Recommended
            </Button>
          </div>
        </div>
      </div>

      <Card className="p-6">
        <form ref={containerRef} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            {article?.isTtsReviewed && (
              <span className="px-2 py-1 text-xs font-semibold bg-green-100 text-green-800">
                Reviewed
              </span>
            )}
            {!article?.isTtsReviewed && (
              <span className="px-2 py-1 text-xs font-semibold bg-red-100 text-red-800">
                Pending Review
              </span>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <span>{article?.title ?? article?.titleUS ?? ''}</span>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title VI</label>
            <span>{article?.titleVI ?? ''}</span>
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between gap-3">
              <label className="block text-sm font-medium text-gray-700">Reel description</label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                loading={isMarkingAsPosted}
                disabled={
                  isLoading ||
                  isMarkingAsPosted ||
                  isRegeneratingImage ||
                  isGeneratingVideo ||
                  regeneratingLevels.size > 0 ||
                  regeneratingIpaLevels.size > 0 ||
                  !article ||
                  article.isFacebookPosted
                }
                onClick={handleMarkAsPosted}
              >
                Mark as Posted
              </Button>
            </div>
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800 whitespace-pre-line">
              {reelDescription}
            </div>
          </div>

          {/* Reel Description*/}

          <div className="mb-6 space-y-3">
            {article?.articleImageSignedUrl && (
              <img
                src={article.articleImageSignedUrl}
                alt={article?.title || article?.titleUS || 'Article'}
                className="w-full max-h-96 object-contain rounded-lg shadow-md"
                loading="eager"
                decoding="async"
                onError={e => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            {!article?.articleImageSignedUrl && (
              <div className="rounded-lg border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500">
                No article image available yet.
              </div>
            )}
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                loading={isRegeneratingImage}
                disabled={
                  isLoading ||
                  isRegeneratingImage ||
                  isGeneratingVideo ||
                  regeneratingLevels.size > 0 ||
                  regeneratingIpaLevels.size > 0 ||
                  !article
                }
                onClick={() => setImageConfirm({ regenerate: true })}
              >
                Re-generate Image
              </Button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              TTS level A ({getLevelLabel('A')})
            </label>
            {renderTtsPlayer(
              'A',
              isVi ? article?.ttsALevelUSSignedUrl : article?.ttsALevelENSignedUrl,
              isVi ? 'US' : 'EN'
            )}
          </div>

          <div className="grid items-start">
            <div>
              <div className="mb-1 flex items-center justify-between gap-3">
                <label className="block text-sm font-medium text-gray-700">
                  Content level A ({getLevelLabel('A')})
                </label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  loading={isRegeneratingSentenceLevel('A')}
                  disabled={
                    isLoading ||
                    isRegeneratingImage ||
                    isRegeneratingLevel('A') ||
                    isRegeneratingArticleOnlyLevel('A') ||
                    isRegeneratingIpaLevel('A') ||
                    !article
                  }
                  onClick={() => {
                    void handleRegenerateLevelSentenceTts('A');
                  }}
                >
                  Re-generate level sentence TTS
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  loading={isRegeneratingIpaLevel('A')}
                  disabled={
                    isLoading ||
                    isRegeneratingImage ||
                    isRegeneratingLevel('A') ||
                    isRegeneratingArticleOnlyLevel('A') ||
                    isRegeneratingSentenceLevel('A')
                  }
                  onClick={() => setIpaConfirm({ level: 'A' })}
                >
                  Re-generate IPA
                </Button>
              </div>
              {!isVi && (
                <>
                  {aSentences.length === 0 && (
                    <div className="font-mono text-sm text-gray-800">
                      {article?.textALevelEN ?? ''}
                    </div>
                  )}
                  {aSentences.map((s, idx) => (
                    <div key={`a-s-${idx}`} className="mb-3">
                      <div className="mb-1 flex items-start gap-2">
                        <div className="flex items-start gap-2">
                          {renderSentencePlayButton('A', idx, s)}
                          <div className="font-mono text-sm text-gray-800">{s}</div>
                        </div>
                      </div>
                      <div className="mt-1">
                        <textarea
                          rows={2}
                          className="w-full font-mono text-sm text-gray-600 px-2 py-1 border rounded resize-y whitespace-pre-wrap"
                          value={ipaAState.en?.[idx] ?? ''}
                          onChange={e => {
                            const copy = (ipaAState.en ?? []).slice();
                            copy[idx] = e.target.value;
                            setIpaAState({ ...ipaAState, en: copy });
                            const ta = e.target as HTMLTextAreaElement;
                            ta.style.height = 'auto';
                            ta.style.height = ta.scrollHeight + 'px';
                          }}
                          placeholder="Edit IPA"
                        />
                      </div>
                    </div>
                  ))}
                </>
              )}
              {isVi && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">US Variant</h4>
                  {aUsSentences.length === 0 && (
                    <div className="font-mono text-sm text-gray-800">
                      {article?.textALevelUS ?? ''}
                    </div>
                  )}
                  {aUsSentences.map((s, idx) => (
                    <div key={`a-us-s-${idx}`} className="mb-3">
                      <div className="mb-1 flex items-start gap-2">
                        <div className="flex items-start gap-2">
                          {renderSentencePlayButton('A', idx, s)}
                          <div className="font-mono text-sm text-gray-800">{s}</div>
                        </div>
                      </div>
                      <div className="mt-1">
                        <textarea
                          rows={2}
                          className="w-full font-mono text-sm text-gray-600 px-2 py-1 border rounded resize-y whitespace-pre-wrap"
                          value={ipaAState.us?.[idx] ?? ''}
                          onChange={e => {
                            const copy = (ipaAState.us ?? []).slice();
                            copy[idx] = e.target.value;
                            setIpaAState({ ...ipaAState, us: copy });
                            const ta = e.target as HTMLTextAreaElement;
                            ta.style.height = 'auto';
                            ta.style.height = ta.scrollHeight + 'px';
                          }}
                          placeholder="Edit IPA (US)"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              TTS level B ({getLevelLabel('B')})
            </label>
            {renderTtsPlayer(
              'B',
              isVi ? article?.ttsBLevelUSSignedUrl : article?.ttsBLevelENSignedUrl,
              isVi ? 'US' : 'EN'
            )}
          </div>
          <div className="grid items-start">
            <div>
              <div className="mb-1 flex items-center justify-between gap-3">
                <label className="block text-sm font-medium text-gray-700">
                  Content level B ({getLevelLabel('B')})
                </label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  loading={isRegeneratingSentenceLevel('B')}
                  disabled={
                    isLoading ||
                    isRegeneratingImage ||
                    isRegeneratingLevel('B') ||
                    isRegeneratingArticleOnlyLevel('B') ||
                    isRegeneratingIpaLevel('B') ||
                    !article
                  }
                  onClick={() => {
                    void handleRegenerateLevelSentenceTts('B');
                  }}
                >
                  Re-generate level sentence TTS
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  loading={isRegeneratingIpaLevel('B')}
                  disabled={
                    isLoading ||
                    isRegeneratingImage ||
                    isRegeneratingLevel('B') ||
                    isRegeneratingArticleOnlyLevel('B') ||
                    isRegeneratingSentenceLevel('B')
                  }
                  onClick={() => setIpaConfirm({ level: 'B' })}
                >
                  Re-generate IPA
                </Button>
              </div>

              {!isVi && (
                <>
                  {bSentences.length === 0 && (
                    <div className="font-mono text-sm text-gray-800">
                      {article?.textBLevelEN ?? ''}
                    </div>
                  )}
                  {bSentences.map((s, idx) => (
                    <div key={`b-s-${idx}`} className="mb-3">
                      <div className="mb-1 flex items-start gap-2">
                        <div className="flex items-start gap-2">
                          {renderSentencePlayButton('B', idx, s)}
                          <div className="font-mono text-sm text-gray-800">{s}</div>
                        </div>
                      </div>
                      <div className="mt-1">
                        <textarea
                          rows={2}
                          className="w-full font-mono text-sm text-gray-600 px-2 py-1 border rounded resize-y whitespace-pre-wrap"
                          value={ipaBState.en?.[idx] ?? ''}
                          onChange={e => {
                            const copy = (ipaBState.en ?? []).slice();
                            copy[idx] = e.target.value;
                            setIpaBState({ ...ipaBState, en: copy });
                            const ta = e.target as HTMLTextAreaElement;
                            ta.style.height = 'auto';
                            ta.style.height = ta.scrollHeight + 'px';
                          }}
                          placeholder="Edit IPA"
                        />
                      </div>
                    </div>
                  ))}
                </>
              )}
              {isVi && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">US Variant</h4>
                  {bUsSentences.length === 0 && (
                    <div className="font-mono text-sm text-gray-800">
                      {article?.textBLevelUS ?? ''}
                    </div>
                  )}
                  {bUsSentences.map((s, idx) => (
                    <div key={`b-us-s-${idx}`} className="mb-3">
                      <div className="mb-1 flex items-start gap-2">
                        <div className="flex items-start gap-2">
                          {renderSentencePlayButton('B', idx, s)}
                          <div className="font-mono text-sm text-gray-800">{s}</div>
                        </div>
                      </div>
                      <div className="mt-1">
                        <textarea
                          rows={2}
                          className="w-full font-mono text-sm text-gray-600 px-2 py-1 border rounded resize-y whitespace-pre-wrap"
                          value={ipaBState.us?.[idx] ?? ''}
                          onChange={e => {
                            const copy = (ipaBState.us ?? []).slice();
                            copy[idx] = e.target.value;
                            setIpaBState({ ...ipaBState, us: copy });
                            const ta = e.target as HTMLTextAreaElement;
                            ta.style.height = 'auto';
                            ta.style.height = ta.scrollHeight + 'px';
                          }}
                          placeholder="Edit IPA (US)"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              TTS level C ({getLevelLabel('C')})
            </label>
            {renderTtsPlayer(
              'C',
              isVi ? article?.ttsCLevelUSSignedUrl : article?.ttsCLevelENSignedUrl,
              isVi ? 'US' : 'EN'
            )}
          </div>
          <div className="grid items-start">
            <div>
              <div className="mb-1 flex items-center justify-between gap-3">
                <label className="block text-sm font-medium text-gray-700">
                  Content level C ({getLevelLabel('C')})
                </label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  loading={isRegeneratingSentenceLevel('C')}
                  disabled={
                    isLoading ||
                    isRegeneratingImage ||
                    isRegeneratingLevel('C') ||
                    isRegeneratingArticleOnlyLevel('C') ||
                    isRegeneratingIpaLevel('C') ||
                    !article
                  }
                  onClick={() => {
                    void handleRegenerateLevelSentenceTts('C');
                  }}
                >
                  Re-generate level sentence TTS
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  loading={isRegeneratingIpaLevel('C')}
                  disabled={
                    isLoading ||
                    isRegeneratingImage ||
                    isRegeneratingLevel('C') ||
                    isRegeneratingArticleOnlyLevel('C') ||
                    isRegeneratingSentenceLevel('C')
                  }
                  onClick={() => setIpaConfirm({ level: 'C' })}
                >
                  Re-generate IPA
                </Button>
              </div>

              {!isVi && (
                <>
                  {cSentences.length === 0 && (
                    <div className="font-mono text-sm text-gray-800">
                      {article?.textCLevelEN ?? ''}
                    </div>
                  )}
                  {cSentences.map((s, idx) => (
                    <div key={`c-s-${idx}`} className="mb-3">
                      <div className="mb-1 flex items-start gap-2">
                        <div className="flex items-start gap-2">
                          {renderSentencePlayButton('C', idx, s)}
                          <div className="font-mono text-sm text-gray-800">{s}</div>
                        </div>
                      </div>
                      <div className="mt-1">
                        <textarea
                          rows={2}
                          className="w-full font-mono text-sm text-gray-600 px-2 py-1 border rounded resize-y whitespace-pre-wrap"
                          value={ipaCState.en?.[idx] ?? ''}
                          onChange={e => {
                            const copy = (ipaCState.en ?? []).slice();
                            copy[idx] = e.target.value;
                            setIpaCState({ ...ipaCState, en: copy });
                            const ta = e.target as HTMLTextAreaElement;
                            ta.style.height = 'auto';
                            ta.style.height = ta.scrollHeight + 'px';
                          }}
                          placeholder="Edit IPA"
                        />
                      </div>
                    </div>
                  ))}
                </>
              )}
              {isVi && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">US Variant</h4>
                  {cUsSentences.length === 0 && (
                    <div className="font-mono text-sm text-gray-800">
                      {article?.textCLevelUS ?? ''}
                    </div>
                  )}
                  {cUsSentences.map((s, idx) => (
                    <div key={`c-us-s-${idx}`} className="mb-3">
                      <div className="mb-1 flex items-start gap-2">
                        <div className="flex items-start gap-2">
                          {renderSentencePlayButton('C', idx, s)}
                          <div className="font-mono text-sm text-gray-800">{s}</div>
                        </div>
                      </div>
                      <div className="mt-1">
                        <textarea
                          rows={2}
                          className="w-full font-mono text-sm text-gray-600 px-2 py-1 border rounded resize-y whitespace-pre-wrap"
                          value={ipaCState.us?.[idx] ?? ''}
                          onChange={e => {
                            const copy = (ipaCState.us ?? []).slice();
                            copy[idx] = e.target.value;
                            setIpaCState({ ...ipaCState, us: copy });
                            const ta = e.target as HTMLTextAreaElement;
                            ta.style.height = 'auto';
                            ta.style.height = ta.scrollHeight + 'px';
                          }}
                          placeholder="Edit IPA (US)"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-4 pt-4 border-t">
            <Button
              type="button"
              onClick={handleSave}
              disabled={
                isLoading ||
                isRegeneratingImage ||
                regeneratingLevels.size > 0 ||
                regeneratingArticleOnlyLevels.size > 0 ||
                regeneratingIpaLevels.size > 0
              }
              className="flex-1"
            >
              {isLoading ? 'Saving...' : 'Save Draft'}
            </Button>
            <Button
              type="button"
              onClick={handleApprove}
              disabled={
                isLoading ||
                isRegeneratingImage ||
                regeneratingLevels.size > 0 ||
                regeneratingArticleOnlyLevels.size > 0 ||
                regeneratingIpaLevels.size > 0
              }
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              Approve TTS
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/editor/articles', { state: { activeTab: 'tts' } })}
              disabled={
                isLoading ||
                isRegeneratingImage ||
                regeneratingLevels.size > 0 ||
                regeneratingArticleOnlyLevels.size > 0 ||
                regeneratingIpaLevels.size > 0
              }
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default TtsReview;
